import type {
  DesktopPraxaChannelManagementBridge,
  PraxaDesktopChannelManagementActionResult,
  PraxaDesktopChannelManagementState,
  PraxaDesktopCreateManagedChannelInput,
  PraxaDesktopSetChannelMemberAccessInput,
  PraxaDesktopUpdateManagedChannelInput,
} from "@praxa/platform-contracts";
import { useCallback, useEffect, useRef, useState } from "react";

export const PRAXA_CHANNEL_MANAGEMENT_LOAD_ERROR =
  "Channel management couldn’t load. Your existing channels are unchanged.";

function getBridge(): DesktopPraxaChannelManagementBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge?.praxaChannelManagement;
}

export function useDesktopPraxaChannelManagement(): {
  readonly action: PraxaDesktopChannelManagementActionResult | null;
  readonly bridge: DesktopPraxaChannelManagementBridge | undefined;
  readonly clearAction: () => void;
  readonly createChannel: (
    input: PraxaDesktopCreateManagedChannelInput,
  ) => Promise<PraxaDesktopChannelManagementActionResult | null>;
  readonly error: string | null;
  readonly loadMembers: (workspaceId: string, channelId: string) => Promise<void>;
  readonly loading: boolean;
  readonly refresh: () => Promise<void>;
  readonly setMemberAccess: (
    input: PraxaDesktopSetChannelMemberAccessInput,
  ) => Promise<PraxaDesktopChannelManagementActionResult | null>;
  readonly state: PraxaDesktopChannelManagementState | null;
  readonly updateChannel: (
    input: PraxaDesktopUpdateManagedChannelInput,
  ) => Promise<PraxaDesktopChannelManagementActionResult | null>;
  readonly working: boolean;
} {
  const bridge = getBridge();
  const [state, setState] = useState<PraxaDesktopChannelManagementState | null>(null);
  const [action, setAction] = useState<PraxaDesktopChannelManagementActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const eventVersionRef = useRef(0);

  const applyState = useCallback((next: PraxaDesktopChannelManagementState) => {
    setState(next);
    setError(null);
  }, []);

  const loadState = useCallback(
    async (
      operation: (
        activeBridge: DesktopPraxaChannelManagementBridge,
      ) => Promise<PraxaDesktopChannelManagementState>,
    ) => {
      if (!bridge) return;
      const eventVersion = eventVersionRef.current;
      setLoading(true);
      setError(null);
      try {
        const next = await operation(bridge);
        if (eventVersionRef.current === eventVersion) applyState(next);
      } catch {
        if (eventVersionRef.current === eventVersion) {
          setError(PRAXA_CHANNEL_MANAGEMENT_LOAD_ERROR);
        }
      } finally {
        setLoading(false);
      }
    },
    [applyState, bridge],
  );

  useEffect(() => {
    if (!bridge) {
      setState(null);
      setAction(null);
      setError(null);
      setLoading(false);
      return;
    }
    let active = true;
    let unsubscribe = () => {};
    try {
      unsubscribe = bridge.onStateChange((next) => {
        eventVersionRef.current += 1;
        if (active) applyState(next);
      });
    } catch {
      setError(PRAXA_CHANNEL_MANAGEMENT_LOAD_ERROR);
    }
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    void bridge
      .getState()
      .then((next) => {
        if (active && eventVersionRef.current === eventVersion) applyState(next);
      })
      .catch(() => {
        if (active && eventVersionRef.current === eventVersion) {
          setError(PRAXA_CHANNEL_MANAGEMENT_LOAD_ERROR);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [applyState, bridge]);

  const invoke = useCallback(
    async (
      operation: (
        activeBridge: DesktopPraxaChannelManagementBridge,
      ) => Promise<PraxaDesktopChannelManagementActionResult>,
    ): Promise<PraxaDesktopChannelManagementActionResult | null> => {
      if (!bridge) return null;
      setWorking(true);
      setAction(null);
      try {
        const result = await operation(bridge);
        setState(result.state);
        setAction(result);
        setError(null);
        return result;
      } catch {
        setError("Praxa couldn’t complete this channel change.");
        return null;
      } finally {
        setWorking(false);
      }
    },
    [bridge],
  );

  return {
    action,
    bridge,
    clearAction: () => setAction(null),
    createChannel: (input) => invoke((activeBridge) => activeBridge.createChannel(input)),
    error,
    loadMembers: (workspaceId, channelId) =>
      loadState((activeBridge) => activeBridge.loadMembers(workspaceId, channelId)),
    loading,
    refresh: () => loadState((activeBridge) => activeBridge.refresh()),
    setMemberAccess: (input) => invoke((activeBridge) => activeBridge.setMemberAccess(input)),
    state,
    updateChannel: (input) => invoke((activeBridge) => activeBridge.updateChannel(input)),
    working,
  };
}
